import * as React from 'react';
import type { TContactParam } from '@cian/newbuilding-chat-component';
import { useDispatch, useSelector } from 'react-redux';

import { requestAuthentication } from '../actions/authentication';
import { getGaLabel } from '../selectors/analytics';
import { getUser } from '../selectors/user';
import { closeBuilderChat, openBuilderChat } from '../slices/builderChat';
import { TDispatch } from '../types/redux';
import { trackOpenChat, trackSendMessageClick } from '../utils/analytics';
import { getBuilders } from '../selectors/newbuilding';

export function useBuilderChat(placeType: string) {
  const dispatch: TDispatch = useDispatch();
  const user = useSelector(getUser);
  const gaLabel = useSelector(getGaLabel);
  const builders = useSelector(getBuilders);

  const openChatWithTracking = React.useCallback(
    (chatContact: TContactParam) => {
      const isBuilder = builders.some(builder => builder.realtyUserId === chatContact.realtyUserId);
      const sellerType = isBuilder ? 'builders' : 'agents';

      trackOpenChat({ label: gaLabel, placeType, sellerType });
      dispatch(openBuilderChat(chatContact));
    },
    [dispatch, gaLabel, placeType, builders],
  );

  const openChat = React.useCallback(
    (chatContact: TContactParam) => {
      const isBuilder = builders.some(builder => builder.realtyUserId === chatContact.realtyUserId);
      const sellerType = isBuilder ? 'builders' : 'agents';

      trackSendMessageClick({ label: gaLabel, placeType, sellerType });

      if (!user.isAuthenticated) {
        dispatch(
          requestAuthentication({
            onSuccess: () => () => openChatWithTracking(chatContact),
            onCloseNoAuth: closeBuilderChat,
          }),
        );

        return;
      }

      openChatWithTracking(chatContact);
    },
    [dispatch, openChatWithTracking, gaLabel, placeType, user, builders],
  );

  return { openChat };
}
